/**
 * Edit recipe types and maths, mirroring `loupe-core/src/recipe.rs`.
 *
 * The editor previews adjustments with CSS filter functions; the backend
 * re-applies the same recipe to the full-resolution master in Rust. Both
 * implementations follow the CSS Filter Effects Module Level 1 spec and the
 * canonical pipeline order:
 *
 *   rotate → flip → crop → brightness → contrast → saturate → hue-rotate
 *          → grayscale → sepia → border
 *
 * Any change to ranges, defaults, or ordering here must be made in the Rust
 * module too.
 */

export const RECIPE_VERSION = 1;

export type Rotation = 0 | 90 | 180 | 270;

export interface CropSpec {
  /** Normalised (0–1) relative to the rotated/flipped image. */
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface BorderSpec {
  aspect_w: number;
  aspect_h: number;
  /** #rrggbb */
  color: string;
  /** Extra padding inside the frame, as a fraction of the image's long edge. */
  margin: number;
}

export interface AdjustSpec {
  /** Multiplier; 1 = identity. Surfaced as "Exposure" in the UI. */
  brightness: number;
  contrast: number;
  saturation: number;
  /** Degrees, -180..180. */
  hue: number;
  /** 0..1 intensity. */
  grayscale: number;
  /** 0..1 intensity. */
  sepia: number;
}

export interface EditRecipe {
  version: number;
  rotate: Rotation;
  flip_h: boolean;
  flip_v: boolean;
  crop?: CropSpec;
  border?: BorderSpec;
  adjust?: AdjustSpec;
}

export const IDENTITY_ADJUST: AdjustSpec = {
  brightness: 1,
  contrast: 1,
  saturation: 1,
  hue: 0,
  grayscale: 0,
  sepia: 0,
};

export function isIdentityAdjust(adjust: AdjustSpec): boolean {
  return (
    adjust.brightness === 1 &&
    adjust.contrast === 1 &&
    adjust.saturation === 1 &&
    adjust.hue === 0 &&
    adjust.grayscale === 0 &&
    adjust.sepia === 0
  );
}

export function isNoopRecipe(recipe: EditRecipe): boolean {
  return (
    recipe.rotate === 0 &&
    !recipe.flip_h &&
    !recipe.flip_v &&
    !recipe.crop &&
    !recipe.border &&
    (!recipe.adjust || isIdentityAdjust(recipe.adjust))
  );
}

/**
 * Validate a recipe against the same rules as the backend. Returns null when
 * valid, otherwise a human-readable reason (the backend would 400 with the
 * same class of error).
 */
export function validateRecipe(recipe: EditRecipe): string | null {
  if (recipe.version !== RECIPE_VERSION) return 'Unsupported recipe version';
  if (![0, 90, 180, 270].includes(recipe.rotate)) return 'Invalid rotation';

  const { crop, border, adjust } = recipe;
  if (crop) {
    const values = [crop.x, crop.y, crop.width, crop.height];
    if (values.some((v) => !Number.isFinite(v))) return 'Invalid crop';
    if (crop.x < 0 || crop.y < 0) return 'Invalid crop';
    if (crop.width < 0.01 || crop.height < 0.01) return 'Crop area is too small';
    if (crop.x + crop.width > 1 + 1e-9 || crop.y + crop.height > 1 + 1e-9) {
      return 'Crop exceeds image bounds';
    }
  }
  if (border) {
    if (!Number.isInteger(border.aspect_w) || border.aspect_w <= 0) return 'Invalid border aspect';
    if (!Number.isInteger(border.aspect_h) || border.aspect_h <= 0) return 'Invalid border aspect';
    const ratio = border.aspect_w / border.aspect_h;
    if (ratio < 0.2 || ratio > 5) return 'Border aspect ratio must be between 1:5 and 5:1';
    if (!/^#[0-9a-fA-F]{6}$/.test(border.color)) return 'Invalid border colour';
    if (!Number.isFinite(border.margin) || border.margin < 0 || border.margin > 0.25) {
      return 'Border margin must be between 0 and 25%';
    }
  }
  if (adjust) {
    const inRange = (v: number, lo: number, hi: number) =>
      Number.isFinite(v) && v >= lo && v <= hi;
    if (!inRange(adjust.brightness, 0, 2)) return 'Exposure out of range';
    if (!inRange(adjust.contrast, 0, 2)) return 'Contrast out of range';
    if (!inRange(adjust.saturation, 0, 2)) return 'Saturation out of range';
    if (!inRange(adjust.hue, -180, 180)) return 'Hue out of range';
    if (!inRange(adjust.grayscale, 0, 1)) return 'Black & white out of range';
    if (!inRange(adjust.sepia, 0, 1)) return 'Sepia out of range';
  }
  if (isNoopRecipe(recipe)) return 'Make at least one change before saving';
  return null;
}

/**
 * CSS filter string for the adjustment chain. The function order here IS the
 * canonical pipeline order — CSS applies filters left to right, and the Rust
 * renderer applies the same steps in the same order.
 */
export function toCssFilter(adjust: AdjustSpec): string {
  const parts: string[] = [];
  if (adjust.brightness !== 1) parts.push(`brightness(${adjust.brightness})`);
  if (adjust.contrast !== 1) parts.push(`contrast(${adjust.contrast})`);
  if (adjust.saturation !== 1) parts.push(`saturate(${adjust.saturation})`);
  if (adjust.hue !== 0) parts.push(`hue-rotate(${adjust.hue}deg)`);
  if (adjust.grayscale !== 0) parts.push(`grayscale(${adjust.grayscale})`);
  if (adjust.sepia !== 0) parts.push(`sepia(${adjust.sepia})`);
  return parts.length > 0 ? parts.join(' ') : 'none';
}

export interface BorderGeometry {
  canvasW: number;
  canvasH: number;
  offsetX: number;
  offsetY: number;
}

/**
 * Letterbox geometry for placing a `width` x `height` image inside a border
 * frame — mirrors `recipe::border_geometry` in Rust (including rounding), so
 * the preview shows the exact frame the render will produce.
 */
export function borderGeometry(
  border: BorderSpec,
  width: number,
  height: number,
): BorderGeometry {
  const pad = Math.round(border.margin * Math.max(width, height));
  const paddedW = width + 2 * pad;
  const paddedH = height + 2 * pad;
  const aspect = border.aspect_w / border.aspect_h;

  let canvasW: number;
  let canvasH: number;
  if (paddedW / paddedH >= aspect) {
    canvasW = paddedW;
    canvasH = Math.round(paddedW / aspect);
  } else {
    canvasW = Math.round(paddedH * aspect);
    canvasH = paddedH;
  }
  canvasW = Math.max(canvasW, paddedW);
  canvasH = Math.max(canvasH, paddedH);

  return {
    canvasW,
    canvasH,
    offsetX: Math.floor((canvasW - width) / 2),
    offsetY: Math.floor((canvasH - height) / 2),
  };
}

/**
 * Largest centred crop of the given aspect ratio (width/height) that fits an
 * image whose own aspect ratio is `imageAspect`, in normalised coordinates.
 */
export function centredCropForAspect(targetAspect: number, imageAspect: number): CropSpec {
  // Normalised units are relative to each dimension, so a crop of visual
  // aspect A on an image of aspect I has width/height = A / I in unit space.
  const unitRatio = targetAspect / imageAspect;
  let width = 1;
  let height = 1;
  if (unitRatio >= 1) {
    height = 1 / unitRatio;
  } else {
    width = unitRatio;
  }
  return {
    x: (1 - width) / 2,
    y: (1 - height) / 2,
    width,
    height,
  };
}

/** Clamp a crop rect inside the unit square, preserving size where possible. */
export function clampCrop(crop: CropSpec): CropSpec {
  const width = Math.min(Math.max(crop.width, 0.01), 1);
  const height = Math.min(Math.max(crop.height, 0.01), 1);
  const x = Math.min(Math.max(crop.x, 0), 1 - width);
  const y = Math.min(Math.max(crop.y, 0), 1 - height);
  return { x, y, width, height };
}

/** Assemble the wire-format recipe, omitting identity sections. */
export function buildRecipe(
  rotate: Rotation,
  flipH: boolean,
  flipV: boolean,
  crop: CropSpec | null,
  border: BorderSpec | null,
  adjust: AdjustSpec,
): EditRecipe {
  const recipe: EditRecipe = {
    version: RECIPE_VERSION,
    rotate,
    flip_h: flipH,
    flip_v: flipV,
  };
  // A full-frame crop is not a crop.
  if (crop && !(crop.x === 0 && crop.y === 0 && crop.width === 1 && crop.height === 1)) {
    recipe.crop = crop;
  }
  if (border) {
    recipe.border = border;
  }
  if (!isIdentityAdjust(adjust)) {
    recipe.adjust = adjust;
  }
  return recipe;
}
